import type {
  CreateWorkspaceRequest,
  UpdateWorkspaceInput,
  WorkspaceWithRole,
} from "@tm/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { workspacesApi } from "../../../api/workspaces.api";
import { queryKeys } from "../../../lib/queryKeys";

export function useWorkspaces() {
  return useQuery({
    queryKey: queryKeys.workspaces,
    queryFn: ({ signal }) => workspacesApi.list(signal),
  });
}

function upsert(
  list: WorkspaceWithRole[] | undefined,
  workspace: WorkspaceWithRole,
): WorkspaceWithRole[] {
  const others = (list ?? []).filter((item) => item.id !== workspace.id);
  return [...others, workspace].sort((a, b) => a.name.localeCompare(b.name));
}

export function useCreateWorkspace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWorkspaceRequest) => workspacesApi.create(input),
    onSuccess: (workspace) => {
      queryClient.setQueryData<WorkspaceWithRole[]>(queryKeys.workspaces, (list) =>
        upsert(list, workspace),
      );
    },
  });
}

export function useUpdateWorkspace(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateWorkspaceInput) => workspacesApi.update(workspaceId, input),
    onSuccess: (workspace) => {
      queryClient.setQueryData<WorkspaceWithRole[]>(queryKeys.workspaces, (list) =>
        upsert(list, workspace),
      );
    },
  });
}

export function useDeleteWorkspace(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => workspacesApi.remove(workspaceId),
    onSuccess: () => {
      queryClient.setQueryData<WorkspaceWithRole[]>(queryKeys.workspaces, (list) =>
        (list ?? []).filter((item) => item.id !== workspaceId),
      );
      queryClient.removeQueries({ queryKey: ["workspaces", workspaceId] });
    },
  });
}

export function useTransferOwnership(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => workspacesApi.transferOwnership(workspaceId, userId),
    onSuccess: async (workspace) => {
      queryClient.setQueryData<WorkspaceWithRole[]>(queryKeys.workspaces, (list) =>
        upsert(list, workspace),
      );
      await queryClient.invalidateQueries({ queryKey: queryKeys.members(workspaceId) });
    },
  });
}
